/**
 * 채점 및 재채점 관리 API 서비스
 * - 학생 결과 조회
 * - 재채점 권한 관리
 */

import axios from '../config/axios';
import type {
  StudentResult,
  AllowRetryRequest,
  AllowRetryResponse,
  AllowRetryBulkResponse,
  PendingGradingResponse,
  GradeSubmissionRequest,
  GradeSubmissionResponse,
  RecentSubmissionsResponse,
} from '../types/grading';

/**
 * 학생의 문제집 채점 결과 조회
 *
 * @param studentId - 학생 ID
 * @param workbookName - 문제집명
 * @returns 학생 채점 결과 (페이지별 상세)
 */
export const getStudentResults = async (
  studentId: string,
  workbookName: string
): Promise<StudentResult> => {
  const response = await axios.get(
    `/instructor/students/${studentId}/workbooks/${encodeURIComponent(workbookName)}/results`
  );
  return response.data;
};

/**
 * 재채점 허락 (페이지 단위)
 *
 * @param request - 재채점 허락 요청
 * @returns 재채점 허락 결과
 */
export const allowRetry = async (
  request: AllowRetryRequest
): Promise<AllowRetryResponse> => {
  const response = await axios.post('/instructor/allow-retry', request);
  return response.data;
};

/**
 * 재채점 권한 취소
 *
 * @param request - 재채점 권한 취소 요청
 * @returns 취소 결과
 */
export const revokeRetry = async (
  request: AllowRetryRequest
): Promise<{ success: boolean; message: string }> => {
  const response = await axios.delete('/instructor/allow-retry', {
    data: request,
  });
  return response.data;
};

/**
 * 일괄 재채점 허락 (문제집 전체)
 * 학생의 해당 문제집에서 오답이 있는 모든 페이지에 대해 재채점 허가
 *
 * @param studentId - 학생 ID
 * @param workbookName - 문제집명
 * @returns 허가된 페이지 수, 총 오답 문제 수
 */
export const allowRetryBulk = async (
  studentId: string,
  workbookName: string
): Promise<AllowRetryBulkResponse> => {
  const response = await axios.post('/instructor/allow-retry-bulk', {
    student_id: studentId,
    workbook_name: workbookName,
  });
  return response.data;
};

/**
 * T타입 채점 대기 목록 조회
 *
 * @param studentId - 학생 ID (선택)
 * @param workbookName - 문제집명 (선택)
 * @param page - 페이지 번호 (선택)
 * @returns 채점 대기 목록
 */
export const getPendingGrading = async (
  studentId?: string,
  workbookName?: string,
  page?: number
): Promise<PendingGradingResponse> => {
  const params: any = {};
  if (studentId) params.student_id = studentId;
  if (workbookName) params.workbook_name = workbookName;
  if (page) params.page = page;

  const response = await axios.get('/instructor/pending-grading', { params });
  return response.data;
};

/**
 * T타입 수동 채점 처리
 *
 * @param submissionId - 제출 ID
 * @param isCorrect - 정답 여부
 * @returns 채점 결과
 */
export const gradeSubmission = async (
  submissionId: string,
  isCorrect: boolean
): Promise<GradeSubmissionResponse> => {
  const response = await axios.post(`/instructor/grade/${submissionId}`, {
    is_correct: isCorrect,
  });
  return response.data;
};

/**
 * 최근 N일간 제출된 페이지 목록 조회
 *
 * @param filter - 필터 옵션 (needs_action | all | completed)
 * @param days - 조회 기간 (일 단위, 기본 2일)
 * @returns 최근 제출 목록 (페이지별 그룹화)
 */
export const getRecentSubmissions = async (
  filter: 'needs_action' | 'all' | 'completed' = 'needs_action',
  days: number = 2
): Promise<RecentSubmissionsResponse> => {
  const response = await axios.get('/instructor/recent-submissions', {
    params: { filter, days },
  });
  return response.data;
};
